import { ArrowRightIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

interface CTASectionProps {
  title: string;
  description: string;
  primaryCTA: {
    text: string;
    onClick: () => void;
  };
  secondaryCTA?: {
    text: string;
    onClick: () => void;
  };
}

export function CTASection({ 
  title, 
  description, 
  primaryCTA, 
  secondaryCTA 
}: CTASectionProps) {
  const trustIndicators = [
    "Free to get started",
    "No credit card required",
    "Secure data handling"
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      }
    }
  };

  const buttonVariants = {
    hover: {
      scale: 1.05,
      y: -3,
      transition: {
        duration: 0.2,
        ease: "easeOut" as const
      }
    },
    tap: {
      scale: 0.98,
      transition: {
        duration: 0.1
      }
    }
  };

  const trustIndicatorVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut" as const
      }
    }
  };

  return (
    <section 
      className="py-16 sm:py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 relative overflow-hidden"
      aria-labelledby="cta-heading"
      role="region"
    >
      {/* Background Pattern */}
      <motion.div 
        className="absolute inset-0 opacity-20"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 0.2 }}
        transition={{ duration: 1 }}
        viewport={{ once: true }}
        aria-hidden="true"
      >
        <div className="w-full h-full bg-white bg-opacity-5" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </motion.div>
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div 
          className="text-center"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {/* Main Content */}
          <motion.h2 
            id="cta-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight"
            variants={itemVariants}
          >
            {title}
          </motion.h2>
          <motion.p 
            className="text-lg sm:text-xl text-blue-100 mb-8 max-w-2xl mx-auto leading-relaxed"
            variants={itemVariants}
            aria-describedby="cta-heading"
          >
            {description}
          </motion.p>

          {/* Trust Indicators */}
          <motion.div 
            className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 mb-10"
            variants={itemVariants}
            role="list"
            aria-label="Trust indicators"
          >
            {trustIndicators.map((indicator, index) => (
              <motion.div 
                key={index} 
                className="flex items-center text-blue-100"
                variants={trustIndicatorVariants}
                custom={index}
                role="listitem"
              >
                <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" aria-hidden="true" />
                <span className="text-sm sm:text-base font-medium">{indicator}</span>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8 max-w-md mx-auto sm:max-w-none"
            variants={itemVariants}
          >
            {/* Primary CTA */}
            <motion.button
              onClick={primaryCTA.onClick}
              className="group bg-white text-blue-700 px-8 py-4 rounded-lg text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 min-w-[200px] min-h-[56px] touch-manipulation w-full sm:w-auto flex items-center justify-center"
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              aria-label="Start tracking your bowling performance now - free to get started"
            >
              <span className="flex items-center justify-center">
                {primaryCTA.text}
                <motion.div
                  animate={{ x: [0, 4, 0] }}
                  transition={{ 
                    duration: 2, 
                    repeat: Infinity, 
                    repeatType: "reverse",
                    ease: "easeInOut"
                  }}
                  aria-hidden="true"
                >
                  <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                </motion.div>
              </span>
            </motion.button>

            {/* Secondary CTA */}
            {secondaryCTA && (
              <motion.button
                onClick={secondaryCTA.onClick}
                className="group border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-blue-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 min-w-[200px] min-h-[56px] touch-manipulation w-full sm:w-auto flex items-center justify-center"
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
                aria-label="Sign in to your existing account"
              >
                {secondaryCTA.text}
              </motion.button>
            )}
          </motion.div>

          {/* Security Badge */}
          <motion.div 
            className="flex items-center justify-center text-blue-100"
            variants={itemVariants}
            role="complementary"
            aria-label="Security indicator"
          >
            <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" aria-hidden="true" />
            <span className="text-sm font-medium">SSL Secured</span>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom Wave */}
      <motion.div 
        className="absolute bottom-0 left-0 right-0"
        initial={{ y: 20, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        viewport={{ once: true }}
      >
        <svg
          className="w-full h-12 sm:h-16 text-white"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          fill="currentColor"
        >
          <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
          />
        </svg>
      </motion.div>
    </section>
  );
}

export default CTASection;